// The AI employees' avatars: the Talkos figure in a pose, with the thing they work with.
// Coordinates are on a 100×100 tile. Colors are roles, not values:
//   body = white, head = ink, tile = the employee's color (also used for cut-outs).
// The app draws the same shapes in app/lib/ui/core/widgets/employee_avatar.dart; keep them in sync.

export const STROKE = 8.5; // the figure's line width, as in the symbol
export const INK = '#16141F';

// Head and body of the figure; `arms` is one polyline through the shoulders.
const figure = ({ cx, armsY, arms, gap = 1.45 }) => [
  { line: arms, width: STROKE },
  { line: [[cx, armsY], [cx, armsY + 2.8 * STROKE]], width: STROKE },
  { circle: [cx, armsY - gap * STROKE, 0.74 * STROKE], fill: 'head' },
];

// A white hard hat on the figure's head: a dome, a brim and a ridge.
const hardHat = ({ cx, armsY, gap = 1.45 }) => {
  const headY = armsY - gap * STROKE;
  const r = 0.74 * STROKE;
  const dome = Array.from({ length: 13 }, (_, i) => {
    const a = Math.PI + (Math.PI * i) / 12;
    return [cx + (r + 0.8) * Math.cos(a), headY - 1 + (r + 2.4) * Math.sin(a)];
  });
  return [
    { polygon: dome, fill: 'body' },
    { line: [[cx - r - 1.6, headY - 1], [cx + r + 2.8, headY - 1]], width: 3.2 },
    { line: [[cx, headY - r - 3.2], [cx, headY - 2]], width: 1.6, stroke: 'tile' },
  ];
};

const starPoints = (cx, cy, r) =>
  Array.from({ length: 10 }, (_, i) => {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 ? r * 0.48 : r;
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  });

const sparkle = (cx, cy, r) => ({ sparkle: [cx, cy, r], fill: 'body' });

// Points along a circle from angle `from` to `to`, in degrees clockwise from three o'clock.
const arc = (cx, cy, r, from, to, steps = 12) =>
  Array.from({ length: steps + 1 }, (_, i) => {
    const a = ((from + ((to - from) * i) / steps) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });

// A headset on the figure's head: a band over it, two ear cushions and a microphone at the mouth.
const headset = ({ cx, armsY, gap = 1.45 }) => {
  const headY = armsY - gap * STROKE;
  const r = 0.74 * STROKE + 3.4;
  return [
    { line: arc(cx, headY, r, 180, 360), width: 3 },
    { rect: [cx - r - 3.2, headY - 3, 6.4, 9.5, 3.2], fill: 'body' },
    { rect: [cx + r - 3.2, headY - 3, 6.4, 9.5, 3.2], fill: 'body' },
    { line: [[cx - r, headY + 5], [cx - 3.5, headY + 5]], width: 2.4 },
    { circle: [cx - 3.2, headY + 5, 2.3], fill: 'body' },
  ];
};

// Each avatar is { figure: [...shapes], item: [...shapes] }; the figure is what moves.
export const avatars = {
  // Rūta hands over an envelope.
  secretary: {
    figure: figure({ cx: 36, armsY: 52, arms: [[21, 62], [28, 52], [57, 52]] }),
    item: [
      { rect: [57, 40, 29, 22, 5], fill: 'body' },
      { line: [[61, 45], [71.5, 52.5], [82, 45]], width: 4, stroke: 'tile' },
    ],
  },
  // Gabija waves with one hand and talks with the other.
  facebook_manager: {
    figure: figure({ cx: 36, armsY: 56, arms: [[20, 44], [28, 56], [44, 56], [52, 47]] }),
    item: [
      { rect: [54, 12, 36, 26, 8], fill: 'body' },
      { polygon: [[60, 36], [68, 36], [58, 45]], fill: 'body' },
      { circle: [64, 25, 2.6], fill: 'tile' },
      { circle: [71.5, 25, 2.6], fill: 'tile' },
      { circle: [79, 25, 2.6], fill: 'tile' },
    ],
  },
  // Rokas holds up an upvote.
  reddit_manager: {
    figure: figure({ cx: 40, armsY: 58, arms: [[29, 68], [32, 58], [54, 58], [54, 41]] }),
    item: [{ polygon: [[54, 13], [65, 25], [59, 25], [59, 33], [49, 33], [49, 25], [43, 25]], fill: 'body', round: 4 }],
  },
  // Aras, hand on hip, points at a rising chart.
  aso_analyst: {
    figure: figure({ cx: 32, armsY: 50, arms: [[25, 63], [17, 56], [24, 50], [40, 50], [48, 42]] }),
    item: [
      { line: [[62, 76], [62, 65]], width: 8 },
      { line: [[73, 76], [73, 54]], width: 8 },
      { line: [[84, 76], [84, 42]], width: 8 },
    ],
  },
  // Linas, in a hard hat, holds up a technical drawing.
  process_engineer: {
    figure: [
      ...figure({ cx: 30, armsY: 56, arms: [[17, 67], [22, 56], [38, 56], [46, 47]] }),
      ...hardHat({ cx: 30, armsY: 56 }),
    ],
    item: [
      { rect: [46, 18, 42, 34, 3.5], fill: 'body' },
      // A bracket with a hole, and its dimension lines.
      { line: [[52.7, 24.1], [76.2, 24.1], [76.2, 31.8], [63.3, 31.8], [63.3, 41.1], [52.7, 41.1], [52.7, 24.1]], width: 2.4, stroke: 'tile' },
      { circle: [57.9, 28.4, 2.6], fill: 'tile' },
      { circle: [57.9, 28.4, 1.1], fill: 'body' },
      { line: [[52.7, 46.6], [76.2, 46.6]], width: 1.6, stroke: 'tile' },
      { line: [[52.7, 44.5], [52.7, 48.6]], width: 1.6, stroke: 'tile' },
      { line: [[76.2, 44.5], [76.2, 48.6]], width: 1.6, stroke: 'tile' },
      { line: [[81.3, 24.1], [81.3, 41.1]], width: 1.6, stroke: 'tile' },
    ],
  },
  // Vaiva lifts a star like a trophy.
  reviews: {
    figure: figure({ cx: 50, armsY: 56, arms: [[35, 40], [41, 56], [59, 56], [65, 40]] }),
    item: [{ polygon: starPoints(50, 21, 12), fill: 'body', round: 3 }],
  },
  // Ugnė, in a headset, waves hello.
  support: {
    figure: [
      ...figure({ cx: 46, armsY: 58, arms: [[33, 70], [38, 58], [58, 58], [64, 40]] }),
      ...headset({ cx: 46, armsY: 58 }),
      { line: arc(64, 40, 10, -80, -25, 6), width: 2.6 },
      { line: arc(64, 40, 15.5, -75, -30, 6), width: 2.6 },
    ],
    item: [],
  },
  // Marius cheers and holds out a target with an arrow in the bullseye.
  sales: {
    figure: figure({ cx: 30, armsY: 60, arms: [[13, 46], [21, 60], [38, 60], [49, 57]] }),
    item: [
      { circle: [69, 50, 16], fill: 'body' },
      { circle: [69, 50, 11.5], fill: 'tile' },
      { circle: [69, 50, 7.5], fill: 'body' },
      { circle: [69, 50, 3.5], fill: 'tile' },
      // The arrow, with a gap cut around its shaft.
      { line: [[69, 50], [84, 35]], width: 7, stroke: 'tile' },
      { line: [[69, 50], [86, 33]], width: 2.8 },
      { line: [[85.4, 37.6], [80.9, 37.6], [80.9, 33.1]], width: 2.8 },
      { line: [[88.2, 34.8], [83.7, 34.8], [83.7, 30.3]], width: 2.8 },
    ],
  },
  // Eglė holds up a receipt.
  bookkeeping: {
    figure: figure({ cx: 68, armsY: 60, arms: [[48, 48], [60, 60], [76, 60], [82, 71]] }),
    item: [
      {
        polygon: [[18, 16], [44, 16], [44, 50], [40.75, 46.5], [37.5, 50], [34.25, 46.5], [31, 50], [27.75, 46.5], [24.5, 50], [21.25, 46.5], [18, 50]],
        fill: 'body',
        round: 3,
      },
      { line: [[23.5, 23], [38.5, 23]], width: 2.6, stroke: 'tile' },
      { line: [[23.5, 29], [32, 29]], width: 2.6, stroke: 'tile' },
      // A euro sign.
      { line: arc(32, 38.5, 5, 45, 315), width: 2.4, stroke: 'tile' },
      { line: [[24.5, 37.2], [31.5, 37.2]], width: 2, stroke: 'tile' },
      { line: [[24.5, 40.2], [31.5, 40.2]], width: 2, stroke: 'tile' },
    ],
  },
  // Justė holds up a camera.
  instagram: {
    figure: figure({ cx: 30, armsY: 62, arms: [[17, 46], [22, 62], [38, 62], [48, 50]] }),
    item: [
      { rect: [56, 17, 12, 8, 2.5], fill: 'body' },
      { rect: [48, 22, 38, 27, 6], fill: 'body' },
      { circle: [67, 35.5, 9.5], fill: 'tile' },
      { circle: [67, 35.5, 6], fill: 'body' },
      { circle: [67, 35.5, 2.6], fill: 'tile' },
      { circle: [80.5, 28, 1.9], fill: 'tile' },
    ],
  },
  // Agnė holds up a calendar page with a booked day.
  bookings: {
    figure: figure({ cx: 32, armsY: 60, arms: [[20, 71], [24, 60], [56, 60], [56, 54]] }),
    item: [
      { rect: [50, 22, 36, 32, 4.5], fill: 'body' },
      { line: [[60, 18], [60, 25]], width: 3.4 },
      { line: [[76, 18], [76, 25]], width: 3.4 },
      { line: [[50, 30.5], [86, 30.5]], width: 2, stroke: 'tile' },
      { line: [[60.5, 41.5], [65.5, 46.5], [75.5, 36.5]], width: 3.6, stroke: 'tile' },
    ],
  },
  // Rasa, hand on hip, holds out a magnifying glass.
  recruiting: {
    figure: figure({ cx: 30, armsY: 60, arms: [[23, 73], [15, 66], [22, 60], [50, 60]] }),
    item: [
      { line: [[50, 60], [58, 51]], width: 6.5 },
      { circle: [66, 42, 12.5], fill: 'body' },
      { circle: [66, 42, 8.3], fill: 'tile' },
      { line: arc(66, 42, 5, 195, 255, 6), width: 2.2 },
    ],
  },
  // Karolis waves and carries a shopping bag.
  eshop: {
    figure: figure({ cx: 30, armsY: 54, arms: [[16, 42], [22, 54], [38, 54], [63, 62]] }),
    item: [
      { line: arc(63, 70, 7, 180, 360), width: 3.2 },
      { polygon: [[50, 68], [76, 68], [79, 89], [47, 89]], fill: 'body', round: 3 },
      { circle: [56, 72, 1.9], fill: 'tile' },
      { circle: [70, 72, 1.9], fill: 'tile' },
    ],
  },
  // Paulius writes on a page with a pencil.
  content: {
    figure: figure({ cx: 26, armsY: 58, arms: [[14, 69], [18, 58], [34, 58], [49.5, 45.5]] }),
    item: [
      { rect: [54, 32, 32, 48, 3.5], fill: 'body' },
      { line: [[61, 41], [79, 41]], width: 2.6, stroke: 'tile' },
      { line: [[61, 49], [79, 49]], width: 2.6, stroke: 'tile' },
      { line: [[61, 57], [79, 57]], width: 2.6, stroke: 'tile' },
      { line: [[61, 65], [79, 65]], width: 2.6, stroke: 'tile' },
      // The pencil, with a gap cut around it where it lies on the page.
      { polygon: [[51.46, 54.85], [55.31, 61.27], [62, 66], [60.97, 57.87], [57.12, 51.45]], fill: 'tile', round: 7 },
      { polygon: [[41.17, 37.7], [55.31, 61.27], [62, 66], [60.97, 57.87], [46.83, 34.3]], fill: 'body', round: 2 },
      { line: [[54.8, 61.58], [61.49, 57.56]], width: 1.6, stroke: 'tile' },
      { polygon: [[59.49, 64.24], [62.51, 66.86], [61.63, 62.96]], fill: 'tile' },
    ],
  },
  // Dovilė calls out through a megaphone.
  ads: {
    figure: figure({ cx: 28, armsY: 60, arms: [[16, 71], [20, 60], [36, 60], [47, 56]] }),
    item: [
      { line: [[48.6, 50], [47.5, 56]], width: 4 },
      { polygon: [[37.92, 43.99], [59.6, 25.77], [68.4, 50.23], [40.08, 50.01]], fill: 'body', round: 3 },
      { line: [[55.59, 27.91], [63.95, 51.14]], width: 1.8, stroke: 'tile' },
      { line: arc(39, 47, 33, -36, -4, 8), width: 3 },
      { line: arc(39, 47, 40, -33, -7, 8), width: 3 },
    ],
  },
};

// Private roles (`Private` in server/internal/catalog/catalog.go) exist only in the organizations that
// asked for them. They are drawn here so the app's copy stays in sync, but kept out of `avatars`:
// build.mjs exports those to the website, which must never show them.
export const privateAvatars = {
  // Domas types on an open laptop with code on the screen.
  developer: {
    figure: figure({ cx: 28, armsY: 58, arms: [[15, 69], [20, 58], [36, 58], [44, 64]] }),
    item: [
      { rect: [50, 34, 38, 27, 3.5], fill: 'body' },
      { polygon: [[46, 64], [92, 64], [95, 69.5], [43, 69.5]], fill: 'body', round: 2.4 },
      // </> on the screen.
      { line: [[61, 42.5], [56.5, 47.5], [61, 52.5]], width: 3, stroke: 'tile' },
      { line: [[77, 42.5], [81.5, 47.5], [77, 52.5]], width: 3, stroke: 'tile' },
      { line: [[71.5, 41.5], [66.5, 53.5]], width: 3, stroke: 'tile' },
    ],
  },
};

// Any other employee: the Talkos figure itself.
export const fallback = {
  figure: figure({ cx: 50, armsY: 46, arms: [[36.4, 46], [63.6, 46]] }),
  item: [],
};

// Moods replace the pose for everyone.
export const moods = {
  happy: {
    figure: figure({ cx: 50, armsY: 56, arms: [[35, 40], [41, 56], [59, 56], [65, 40]] }),
    item: [sparkle(22, 26, 7), sparkle(79, 22, 9), sparkle(82, 44, 4.5)],
  },
  sleepy: {
    figure: figure({ cx: 42, armsY: 54, arms: [[30, 67], [35, 54], [49, 54], [54, 67]], gap: 1.3 }),
    item: [
      { line: [[60, 22], [70, 22], [60, 32], [70, 32]], width: 4 },
      { line: [[74, 12], [81, 12], [74, 19], [81, 19]], width: 3.2 },
    ],
  },
};

// ── SVG ────────────────────────────────────────────────────────────────────────────────────
const n = (v) => Math.round(v * 100) / 100;
const pts = (points) => points.map(([x, y]) => `${n(x)},${n(y)}`).join(' ');
const paint = (role, tileColor) => (role === 'head' ? INK : role === 'tile' ? tileColor : '#FFFFFF');

const sparklePath = ([cx, cy, r]) =>
  `M${n(cx)} ${n(cy - r)}Q${n(cx)} ${n(cy)} ${n(cx + r)} ${n(cy)}Q${n(cx)} ${n(cy)} ${n(cx)} ${n(cy + r)}` +
  `Q${n(cx)} ${n(cy)} ${n(cx - r)} ${n(cy)}Q${n(cx)} ${n(cy)} ${n(cx)} ${n(cy - r)}Z`;

const shapeSvg = (s, tileColor) => {
  if (s.line) {
    return `<polyline points="${pts(s.line)}" fill="none" stroke="${paint(s.stroke ?? 'body', tileColor)}" stroke-width="${s.width}" stroke-linecap="round" stroke-linejoin="round"/>`;
  }
  if (s.circle) {
    const [cx, cy, r] = s.circle;
    return `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}" fill="${paint(s.fill, tileColor)}"/>`;
  }
  if (s.rect) {
    const [x, y, w, h, r] = s.rect;
    return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${paint(s.fill, tileColor)}"/>`;
  }
  if (s.polygon) {
    const c = paint(s.fill, tileColor);
    const round = s.round ? ` stroke="${c}" stroke-width="${s.round}" stroke-linejoin="round"` : '';
    return `<polygon points="${pts(s.polygon)}" fill="${c}"${round}/>`;
  }
  if (s.sparkle) return `<path d="${sparklePath(s.sparkle)}" fill="${paint(s.fill, tileColor)}"/>`;
  throw new Error(`unknown shape ${JSON.stringify(s)}`);
};

// Inner markup without the tile, grouped so the figure can be animated on its own.
export const avatarMarkup = (avatar, tileColor = 'currentColor') =>
  `<g class="fig">${avatar.figure.map((s) => shapeSvg(s, tileColor)).join('')}</g>` +
  `<g class="item">${avatar.item.map((s) => shapeSvg(s, tileColor)).join('')}</g>`;

export const avatarSvg = (avatar, color) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="30" fill="${color}"/>${avatarMarkup(avatar, color)}</svg>`;
